import getConfig from "next/config";
import { useEffect, useState } from "react";
import type { Event } from "src/types";

import { analyticsIdentify } from "@/features/analytics/analytics-reducer";
import { initAnalyticsClient } from "@/features/analytics/setup";
import { useAppDispatch } from "@/redux/store";
import { initDatadogRum } from "@/utils/init-datadog-rum";
const { publicRuntimeConfig } = getConfig();
import { useFlags } from "launchdarkly-react-client-sdk";
import { Helmet } from "react-helmet";

import TrackingProviders from "./tracking-providers";

type ConsentDecision = "ACCEPT" | "DENY";

/**
 * This is a one-time event, meaning the callback will be called at most once.
 * The event will dispatch when the ANALYTICS consent is given (the value becomes ACCEPT).
 */

interface Consent {
  ANALYTICS: ConsentDecision;
  MARKETING: ConsentDecision;
  PERSONALIZATION: ConsentDecision;
  ESSENTIAL: ConsentDecision;
}

const OneTrustCookieCategoryCode = {
  StrictlyNecessary: "C0001",
  Performance: "C0002",
  Functional: "C0003",
  Targeting: "C0004",
  SocialMedia: "C0005",
  Video: "C0006",
};

type AnalyticsConsentManagerProps = {
  event: Event;
};

export const AnalyticsConsentManager = ({
  event,
}: AnalyticsConsentManagerProps) => {
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const { oneTrustCookieConsentEnabled: useOneTrust } = flags;
  const useEdrAnalytics = Boolean((flags as any).canvasEnableEdrTracking);
  const isUseOneTrustFlagDefined = useOneTrust !== undefined;
  const isRingCentralDomain =
    typeof window !== "undefined" &&
    window?.location?.hostname?.endsWith("ringcentral.com");

  useEffect(() => {
    if (
      !isUseOneTrustFlagDefined ||
      !useOneTrust ||
      (Boolean(flags.customDomainsV3) && !isRingCentralDomain)
    )
      return;

    const handleOneTrustUpdate = () => {
      if (
        window.OnetrustActiveGroups?.includes(
          OneTrustCookieCategoryCode.Performance
        )
      ) {
        initDatadogRum();
        if (useEdrAnalytics) {
          initAnalyticsClient();
        }
        setAnalyticsConsent(true);
        dispatch(analyticsIdentify());
      }
    };

    window.addEventListener("OneTrustUpdate", handleOneTrustUpdate);

    return () => {
      window.removeEventListener("OneTrustUpdate", handleOneTrustUpdate);
    };
  }, [
    flags.customDomainsV3,
    isRingCentralDomain,
    isUseOneTrustFlagDefined,
    useOneTrust,
    useEdrAnalytics,
    dispatch,
  ]);

  if (
    !isUseOneTrustFlagDefined ||
    (Boolean(flags.customDomainsV3) && !isRingCentralDomain)
  ) {
    return null;
  }

  return (
    <>
      <Helmet>
        <script type="text/javascript">
          {`
            window.analyticsAllowed = function() { 
              return window.OnetrustActiveGroups?.includes('C0004'); 
            };
          `}
        </script>
      </Helmet>
      {useOneTrust && publicRuntimeConfig.DISABLE_COOKIE_CONSENT !== "true" ? (
        <Helmet>
          <script
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            type="text/javascript"
            charSet="UTF-8"
            data-domain-script="6ac561ff-e2a0-4962-abc5-fc5fab405159"
          ></script>
          <script type="text/javascript">
            {`
          function OptanonWrapper() {
            const evt = new Event('OneTrustUpdate');
            window.dispatchEvent(evt);
            
            // OneTrust change
            setTimeout(() => {
              if(document.body.classList.contains('registration-modal-open')) {
                window.OneTrust?.Close();
                document.body.classList.remove('registration-modal-open');
              }
            }, 0);

            window.OneTrust?.OnConsentChanged(function() {
              window.dispatchEvent(evt);
            }) 
          }
            `}
          </script>
        </Helmet>
      ) : null}
      {analyticsConsent && <TrackingProviders event={event} />}
    </>
  );
};
